import { Icon, IconName } from '@usealloy/component-library';
import React, { createContext, useContext, useMemo } from 'react';
import styled from 'styled-components';

import { Collapsible } from './Collapsible';
import { SidebarButtonBase } from './SidebarButtonBase';
import { useSidebarCtx } from './SidebarContext';
import { useControllableState } from './hooks/useControllableState';
import { bodySmallText, nonInputFocus, truncateText } from './utils/mixins';

// ============================================================
//     SIDEBAR GROUP - COLLAPSIBLE CONTEXT
// ============================================================
// Internal only — not exported. Lets `GroupLabel`/`GroupContent` know whether their parent
// `Group` opted into `collapsible` behavior, without requiring `GroupLabel` to take on any new
// public props.
interface GroupCollapsibleState {
  isOpen: boolean;
  toggleOpen: () => void;
}

const GroupCollapsibleCtx = createContext<GroupCollapsibleState | null>(null);

// ============================================================
//     SIDEBAR GROUP - WRAPPER
// ============================================================
const StyledGroup = styled.div({
  position: 'relative',
  width: '100%',
});

interface GroupProps extends React.ComponentPropsWithoutRef<'div'> {
  /**
   * Enables collapse/expand behavior for this group.
   *
   * When set, `Sidebar.GroupLabel` becomes a clickable/keyboard-operable toggle (with a
   * rotating disclosure icon), and `Sidebar.GroupContent` shows/hides accordingly.
   *
   * @default false
   */
  collapsible?: boolean;

  /**
   * The initial open state when `collapsible` and uncontrolled (no `open` prop provided).
   *
   * @default true
   */
  defaultOpen?: boolean;

  /**
   * Called when the user toggles the group open/closed. Only relevant when `collapsible`.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Controls the open state externally. Only relevant when `collapsible`.
   *
   * **NOTE**: Must be used in conjunction with `onOpenChange`.
   */
  open?: boolean;
}

/**
 * Logical grouping container for organizing related menu items.
 *
 * Use to separate navigation into distinct sections with optional labels and actions.
 *
 * #### Important Props:
 * - `collapsible` - enables click-to-collapse behavior via `Sidebar.GroupLabel` and `Sidebar.GroupContent`
 *
 * @example
 * ```tsx
 * <Sidebar.Group>
 *   <Sidebar.GroupLabel>Navigation</Sidebar.GroupLabel>
 *   <Sidebar.Menu>
 *     <Sidebar.MenuItem>
 *       <Sidebar.MenuButton>Home</Sidebar.MenuButton>
 *     </Sidebar.MenuItem>
 *   </Sidebar.Menu>
 * </Sidebar.Group>
 * ```
 *
 * @example Collapsible group
 * ```tsx
 * <Sidebar.Group collapsible open={isOpen} onOpenChange={setIsOpen}>
 *   <Sidebar.GroupLabel>Navigation</Sidebar.GroupLabel>
 *   <Sidebar.GroupContent>
 *     <Sidebar.Menu>
 *       <Sidebar.MenuItem>
 *         <Sidebar.MenuButton>Home</Sidebar.MenuButton>
 *       </Sidebar.MenuItem>
 *     </Sidebar.Menu>
 *   </Sidebar.GroupContent>
 * </Sidebar.Group>
 * ```
 */
export const Group = React.forwardRef<HTMLDivElement, GroupProps>((props, forwardedRef) => {
  const {
    collapsible = false, defaultOpen = true, onOpenChange, open, ...restProps
  } = props;
  const { isExpanded } = useSidebarCtx();
  const [isOpen, setIsOpen] = useControllableState({
    defaultProp: defaultOpen,
    onChange: onOpenChange,
    prop: open,
  });

  // A per-group collapsed preference must never hide a MenuButton's icon while the whole
  // sidebar is in icon-only rail mode — so when `!isExpanded`, force the group open and make
  // `toggleOpen` a no-op, without touching the underlying `isOpen`/`onOpenChange` state. That way
  // the group's own preference is preserved and simply reasserts itself once the sidebar expands
  // again, instead of being clobbered by the forced-open render.
  const isEffectivelyOpen = isExpanded ? isOpen : true;

  const collapsibleCtxValue = useMemo((): GroupCollapsibleState => ({
    isOpen: isEffectivelyOpen,
    toggleOpen: () => {
      // GroupLabel is inert while icon-only, so this guard is only defensive.
      /* v8 ignore next -- defensive: only reachable via CollapsibleGroupLabel, which itself only renders while isExpanded is true */
      if (!isExpanded) return;
      setIsOpen(!isOpen);
    },
  }), [isEffectivelyOpen, isExpanded, isOpen, setIsOpen]);

  const group = (
    <StyledGroup {...restProps} ref={forwardedRef} data-component="Sidebar.Group" />
  );

  if (!collapsible) return group;

  return (
    <GroupCollapsibleCtx.Provider value={collapsibleCtxValue}>
      {group}
    </GroupCollapsibleCtx.Provider>
  );
});

Group.displayName = 'Sidebar.Group' as const;

// ============================================================
//     SIDEBAR GROUP - LABEL
// ============================================================
const StyledGroupLabel = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--cl-space-2)',
  flex: '0 0 auto',

  width: 'calc(100% + 12px)',
  paddingBlock: 'var(--cl-space-1)',
  paddingInline: 'var(--cl-space-1)',

  color: 'var(--cl-text-muted)',
  marginLeft: '-12px',
  ...bodySmallText,

  '& > span': {
    ...truncateText,
  },

  '&[data-expanded="false"]': {
    paddingInline: 0,
    justifyContent: 'center',

    // We transition BOTH padding and justify content when moving to collapsed, but only transition padding when moving to expanded
    transitionProperty: 'padding-inline, justify-content',
    transitionDelay: '250ms',
  },

  transitionProperty: 'padding-inline',
  transitionDuration: 'var(--cl-duration-300)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',

  '&[data-interactive]': {
    cursor: 'pointer',
    borderRadius: 'var(--cl-radius-1)',
    ...nonInputFocus,

    '&:hover': {
      backgroundColor: 'var(--cl-background-tertiary-hovered)',
    },
  },
});

const StyledDisclosureIcon = styled.span({
  display: 'inline-flex',
  flex: '0 0 auto',

  '&[data-open="true"]': {
    transform: 'rotate(90deg)',
  },

  transitionProperty: 'transform',
  transitionDuration: 'var(--cl-duration-300)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
});

// Only rendered when the parent `Sidebar.Group` is `collapsible` — see `GroupLabel` below, which
// branches on `GroupCollapsibleCtx` to pick this vs. the plain (unchanged) label render path.
interface CollapsibleGroupLabelProps extends React.ComponentPropsWithoutRef<'div'> {
  collapsibleState: GroupCollapsibleState;
  isExpanded: boolean;
}

const CollapsibleGroupLabel = React.forwardRef<
HTMLDivElement,
CollapsibleGroupLabelProps
>((props, forwardedRef) => {
  const {
    children, collapsibleState, isExpanded, onClick, onKeyDown, ...restProps
  } = props;
  const { isOpen, toggleOpen } = collapsibleState;

  return (
    <StyledGroupLabel
      {...restProps}
      ref={forwardedRef}
      aria-expanded={isOpen}
      data-component="Sidebar.GroupLabel"
      data-expanded={isExpanded}
      data-interactive=""
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        toggleOpen();
        if (!event.defaultPrevented) onClick?.(event);
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleOpen();
        }
        if (!event.defaultPrevented) onKeyDown?.(event);
      }}
      role="button"
      tabIndex={0}
    >
      <StyledDisclosureIcon data-open={isOpen}>
        <Icon icon={IconName.CaretRight} size="small" />
      </StyledDisclosureIcon>
      {children}
    </StyledGroupLabel>
  );
});

CollapsibleGroupLabel.displayName = 'Sidebar.GroupLabel' as const;

/**
 * Heading/label for a `Sidebar.Group` section.
 *
 * Renders muted text that identifies the group's purpose.
 *
 * NOTE: Wrap text content in a `<span>` for proper text truncation when the sidebar is collapsed.
 *
 * When the parent `Sidebar.Group` has `collapsible` set, this becomes a clickable/keyboard-operable
 * toggle with a rotating disclosure icon — no extra props needed here.
 *
 * **CAUTION**: don't combine a `collapsible` `Sidebar.Group` with `Sidebar.GroupAction` inside the
 * same label — that would nest an interactive button inside this label's own button role.
 *
 * @example
 * ```tsx
 * <Sidebar.Group>
 *   <Sidebar.GroupLabel>
 *     <span>Navigation</span>
 *   </Sidebar.GroupLabel>
 *   <Sidebar.Menu>
 *   // ...Menu Items
 *   </Sidebar.Menu>
 * </Sidebar.Group>
 * ```
 */
export const GroupLabel = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>((props, forwardedRef) => {
  const { isExpanded } = useSidebarCtx();
  const collapsibleState = useContext(GroupCollapsibleCtx);

  // Non-collapsible groups, and collapsible groups while the sidebar is icon-only, render an
  // inert label — no button role / keyboard toggle. Icon-only already force-opens content via
  // `Group`; an interactive no-op toggle here would be a confusing affordance.
  if (!collapsibleState || !isExpanded) {
    return (
      <StyledGroupLabel {...props} ref={forwardedRef} data-component="Sidebar.GroupLabel" data-expanded={isExpanded} />
    );
  }

  return (
    <CollapsibleGroupLabel
      {...props}
      ref={forwardedRef}
      collapsibleState={collapsibleState}
      isExpanded={isExpanded}
    />
  );
});

GroupLabel.displayName = 'Sidebar.GroupLabel' as const;

// ============================================================
//     SIDEBAR GROUP - ACTION
// ============================================================
interface GroupActionProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'children'> {
  /**
   * The specific icon to render for the action button
   */
  iconName: IconName;

  /**
   * Required for accessibility
   */
  label: string;
}

const StyledGroupAction = styled(SidebarButtonBase)({
  display: 'flex',
  height: 'var(--cl-space-4)',
  width: 'var(--cl-space-4)',
  border: 'none',
  marginRight: 'var(--cl-space-2)',

  '--btn-bg': 'var(--cl-background-tertiary)',
  '--btn-bg-hover': 'var(--cl-background-tertiary-hovered)',
  '--btn-bg-active': 'var(--cl-background-tertiary-active)',

  '&[data-expanded="false"]': {
    display: 'none',
  },
});

/**
 * Action button displayed within a `Sidebar.GroupLabel` header, adjacent to any text content.
 *
 * Hidden when the sidebar is collapsed.
 * Useful for a group-level action, like "Add new item" or "Expand all".
 *
 * #### Important Props:
 * - `iconName` - the icon to display
 * - `label` - required accessible label for the button
 *
 * @example
 * ```tsx
 * <Sidebar.Group>
 *   <Sidebar.GroupLabel>
 *     <span>Navigation</span>
 *     <Sidebar.GroupAction iconName={IconName.Plus} label="Add new item" />
 *   </Sidebar.GroupLabel>
 *   <Sidebar.Menu>
 *   // ...Menu Items
 *   </Sidebar.Menu>
 * </Sidebar.Group>
 * ```
 */
export const GroupAction = (props: GroupActionProps) => {
  const { iconName, label, ...restProps } = props;
  const { isExpanded } = useSidebarCtx();

  return (
    <StyledGroupAction
      type="button"
      {...restProps}
      aria-label={label}
      data-component="Sidebar.GroupAction"
      data-expanded={isExpanded}
      data-icon-only=""
      data-size="xsmall"
    >
      <Icon icon={iconName} size="small" />
    </StyledGroupAction>
  );
};

GroupAction.displayName = 'Sidebar.GroupAction' as const;

// ============================================================
//     SIDEBAR GROUP - CONTENT
// ============================================================
/**
 * Wraps the collapsible contents of a `Sidebar.Group` (typically `Sidebar.Menu`).
 *
 * Only meaningful when the parent `Sidebar.Group` has `collapsible` set — requires a
 * `Sidebar.Group collapsible` ancestor to provide its open state.
 *
 * @example
 * ```tsx
 * <Sidebar.Group collapsible open={isOpen} onOpenChange={setIsOpen}>
 *   <Sidebar.GroupLabel>
 *     <span>Navigation</span>
 *   </Sidebar.GroupLabel>
 *   <Sidebar.GroupContent>
 *     <Sidebar.Menu>
 *     // ...Menu Items
 *     </Sidebar.Menu>
 *   </Sidebar.GroupContent>
 * </Sidebar.Group>
 * ```
 */
interface GroupContentProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
}

export const GroupContent = (props: GroupContentProps) => {
  const collapsibleState = useContext(GroupCollapsibleCtx);

  if (!collapsibleState) {
    throw new Error('`Sidebar.GroupContent` must be used within a collapsible `Sidebar.Group`');
  }

  const { isOpen } = collapsibleState;

  // `Collapsible` already renders its own `data-component="Collapsible"` marker on the wrapping
  // element (no `Collapsible.Content` subcomponent split to forward to, unlike jslib/ui's atom) —
  // this mirrors how `useCollapsibleNavItem`'s consumers already use `Collapsible` directly at the
  // item level.
  return <Collapsible open={isOpen} {...props} />;
};

GroupContent.displayName = 'Sidebar.GroupContent' as const;
